import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarPerson } from '@factorialco/f0-react'
import { ProjectionKind, useTrainingRequestsDatatable } from 'generated/resources/datatable.graphql'
import {
  ApplicantForRequestsDataTableFragment,
  CompetencyFragment,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'
import moment from 'moment'

import DataTable from 'modules/core/components/DataTable'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { CompetenciesTags } from 'modules/trainings/components/MyTrainings/RequestsComponent/MyRequestsDataTable/CompetenciesTags'
import { StatusTag } from 'modules/trainings/components/MyTrainings/RequestsComponent/MyRequestsDataTable/StatusTag'

import { Participants } from '../Participants'
import { ActionsDropdown } from './ActionsDropdown'
import { applicantFilter } from './applicantFilter'
import { competenciesFilter } from './competenciesFilter'
import { dateFilter } from './dateFilter'
import { statusFilter } from './statusFilter'

interface Props {
  search: string
  requests: ApplicantForRequestsDataTableFragment[]
  competenciesData: CompetencyFragment[]
}
export const Provider = ({
  search,
  children,
  requests,
  competenciesData,
}: React.PropsWithChildren<Props>) => {
  const { navigation } = useNavigation()

  const getStatusTemplate = () => {
    return (
      '{% case status %}{% when "approved" %}' +
      i18n.t('trainings.training_requests.my_requests.datatable.status_tag.approved') +
      '{% when "draft" %}' +
      i18n.t('trainings.training_requests.my_requests.datatable.status_tag.draft') +
      '{% when "rejected" %}' +
      i18n.t('trainings.training_requests.my_requests.datatable.status_tag.rejected') +
      '{% when "review" %}' +
      i18n.t('trainings.training_requests.my_requests.datatable.status_tag.pending') +
      '{% else %}{{ status }}{% endcase %}'
    )
  }

  const source = useTrainingRequestsDatatable(
    [
      {
        identifier: 'trainingName',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.training_requests.my_requests.datatable.columns.training_name'),
        render: ({ trainingName }) => {
          return trainingName
        },
        template: '{{ trainingName }}',
        width: 220,
      },
      {
        identifier: 'createdAt',
        kind: ProjectionKind.Custom,
        sortBy: 'createdAt',
        title: i18n.t('trainings.training_requests.my_requests.datatable.columns.request_date'),
        render: ({ createdAt }) => {
          const date = moment(createdAt)
          return <Box>{dateToString(date, 'ui')}</Box>
        },
        template: '{{ createdAt }}',
        width: 140,
      },
      {
        hidden: true,
        identifier: 'competencies',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.training_requests.my_requests.datatable.columns.competencies'),
        render: ({ competencies }) => {
          return <CompetenciesTags competencies={competencies} />
        },
        template: '{{ competencyIds }}',
        width: 180,
      },
      {
        identifier: 'authorId',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.training_requests.requests.datatable.requested_by'),
        render: ({ author }) => {
          return (
            <Box gap="s16" flexDirection="row" alignItems="center">
              <F0AvatarPerson
                firstName={author?.fullName ?? ''}
                lastName=""
                src={author?.avatar ?? ''}
                size="medium"
              />
              <Text size="300">{author?.fullName}</Text>
            </Box>
          )
        },
        template: '{{ author.fullName }}',
        width: 180,
      },
      {
        identifier: 'participants',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.training_requests.requests.datatable.participants'),
        render: ({ participants }) => <Participants employees={participants} />,
        template:
          '{% for participant in participants -%}{{ participant.fullName }}{%- unless forloop.last -%}, {%- endunless -%}{%- endfor %}',
        width: 180,
      },
      {
        identifier: 'status',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.training_requests.requests.datatable.status'),
        render: ({ status }) => <StatusTag status={status} />,
        template: getStatusTemplate(),
        width: 120,
      },
      {
        identifier: 'cost',
        kind: ProjectionKind.Path,
        title: i18n.t('trainings.training_requests.create.cost'),
        path: 'cost',
        width: 160,
      },
      {
        width: 120,
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        render: ({ id, status, currentApproverIds }) => {
          return (
            <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
              <ActionsDropdown
                id={id}
                status={status}
                currentApproverIds={compact(currentApproverIds)}
              />
              <Button.Router
                type="secondary"
                to={navigation.trainings.requests.detail.view.fullPath({ id: String(id) })}
                iconName="arrow-right"
                onClick={() =>
                  track(trackingEvents.training.trainingRequest.goToTrainingRequest, {
                    from: 'requests',
                  })
                }
              />
            </Box>
          )
        },
      },
    ],
    {
      variables: {
        search,
      },
      defaultSort: 'createdAt',
    }
  )

  return (
    <DataTable.Provider
      sort={{
        path: 'createdAt',
        direction: 'desc',
      }}
      filters={[
        applicantFilter(source, requests),
        competenciesFilter(source, competenciesData),
        dateFilter(source),
        statusFilter(source),
      ]}
      source={source}
    >
      {children}
    </DataTable.Provider>
  )
}
